import { useEffect, useState, ReactElement } from "react";
import {
  Wizard,
  FormField,
  Input,
  Select,
  Toggle,
  SpaceBetween,
  Header,
  Container,
  Alert,
  Box,
  ColumnLayout,
  BreadcrumbGroup,
  AttributeEditor,
  ExpandableSection,
  Button,
  Spinner,
} from "@cloudscape-design/components";
import { useNavigate, useParams } from "react-router-dom";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { getProductSchema, FieldSchema } from "./productSchemas";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface Environment {
  documentId: string;
  name: string;
  isProduction: boolean;
}

interface ProjectInfo {
  documentId: string;
  name: string;
  environments: Environment[];
}

interface KeyValuePair {
  key: string;
  value: string;
}

/** A single item's field values inside a "group-list" (e.g. one Lambda Container environment).
 * Recursive because a group-list item can itself contain nested keyvalue/group-list fields. */
type FieldValue = string | number | boolean | KeyValuePair[] | GroupItem[];
type GroupItem = Record<string, FieldValue>;
type FormValues = GroupItem;

function defaultsForFields(fields: FieldSchema[]): GroupItem {
  const values: GroupItem = {};
  for (const field of fields) {
    if (field.type === "keyvalue") values[field.key] = [];
    else if (field.default !== undefined) values[field.key] = field.default;
    else if (field.type === "toggle") values[field.key] = false;
    else values[field.key] = "";
  }
  return values;
}

function defaultsFor(schema: ReturnType<typeof getProductSchema>): FormValues {
  const values: FormValues = {};
  if (!schema) return values;
  for (const step of schema.steps) {
    for (const field of step.fields) {
      if (field.type === "group-list") values[field.key] = [];
      else if (field.type === "keyvalue") values[field.key] = [];
      else if (field.default !== undefined) values[field.key] = field.default;
      else if (field.type === "toggle") values[field.key] = false;
      else values[field.key] = "";
    }
  }
  return values;
}

function isFieldVisible(field: FieldSchema, values: GroupItem | FormValues): boolean {
  if (!field.visibleWhen) return true;
  const actual = values[field.visibleWhen.key];
  const expected = field.visibleWhen.value;
  return Array.isArray(expected) ? expected.includes(actual as any) : actual === expected;
}

function isStepVisible(step: { visibleWhen?: FieldSchema["visibleWhen"] }, values: FormValues): boolean {
  if (!step.visibleWhen) return true;
  const actual = values[step.visibleWhen.key];
  const expected = step.visibleWhen.value;
  return Array.isArray(expected) ? expected.includes(actual as any) : actual === expected;
}

/** Applies the "turning off a toggle hides an injectsEnvVars field, so strip what it injected"
 * cleanup rule to a flat field-value bag (used for both the top-level form and group-list items). */
function withInjectedEnvVarsCleanup(fields: FieldSchema[], next: GroupItem): GroupItem {
  for (const field of fields) {
    if (!field.injectsEnvVars) continue;
    if (isFieldVisible(field, next) || !next[field.key]) continue;
    const { targetKey, vars } = field.injectsEnvVars;
    const varKeys = Object.keys(vars);
    const currentPairs = (next[targetKey] as KeyValuePair[] | undefined) ?? [];
    next[targetKey] = currentPairs.filter((p) => !varKeys.includes(p.key));
    next[field.key] = false;
  }
  return next;
}

/** Writes `value` into `obj` at the given dot-path, creating intermediate objects as needed. */
function setAtPath(obj: Record<string, any>, path: string, value: any) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (typeof cur[key] !== "object" || cur[key] === null) cur[key] = {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
}

/** Builds the payload contribution of a single field (scalar, keyvalue, or group-list) given
 * its current value, writing into `payload` at `basePath` + the field's own `payloadPath`. */
function buildFieldPayload(field: FieldSchema, value: FieldValue | undefined, payload: Record<string, any>) {
  if (field.uiOnly) return; // drives conditional rendering only; has no payload property of its own
  if (field.type === "keyvalue") {
    const pairs = (value as KeyValuePair[] | undefined) ?? [];
    const validPairs = pairs.filter((p) => p.key !== "");
    if (validPairs.length === 0) return;
    const obj: Record<string, string> = {};
    for (const p of validPairs) obj[p.key] = p.value;
    setAtPath(payload, field.payloadPath, obj);
    return;
  }
  if (field.type === "group-list") {
    const items = (value as GroupItem[] | undefined) ?? [];
    const itemFields = field.itemFields ?? [];
    const rendered = items.map((item) => {
      const itemPayload: Record<string, any> = {};
      for (const f of itemFields) {
        if (!isFieldVisible(f, item)) continue;
        if (f.injectsEnvVars) continue;
        buildFieldPayload(f, item[f.key], itemPayload);
      }
      return itemPayload;
    });
    setAtPath(payload, field.payloadPath, rendered);
    return;
  }
  let v: any = value;
  if (v === undefined || v === "") return;
  if (field.numeric) v = Number(v);
  if (field.valueMap && Object.prototype.hasOwnProperty.call(field.valueMap, String(v))) v = field.valueMap[String(v)];
  if (field.omitIfEquals !== undefined && v === field.omitIfEquals) return;
  setAtPath(payload, field.payloadPath, v);
}

export interface CreateProductPageProps {
  /** "edit" prefills the form from `initialValues`, locks Project Name/Account, sends
   * `operationType: 'update'` on submit, and navigates back to the stack on success, instead of
   * the "create" defaults (blank form, editable Project Name/Account, `operationType: 'deploy'`,
   * navigate to /projects). Defaults to "create" so existing <CreateProductPage /> usages (the
   * /create/:productType route) are unaffected. */
  mode?: "create" | "edit";
  /** Required for mode="edit": the product type to render, since edit routes don't carry it in
   * the URL param the way /create/:productType does. Falls back to the :productType route param
   * otherwise. */
  productTypeOverride?: string;
  /** Prefilled form values for mode="edit", typically from `formValuesFromPayload`. */
  initialValues?: FormValues;
  /** The project this product belongs to, when already known ahead of time. In "edit" mode this
   * is passed by EditProductPage from its :projectId route param (the stack's project can't be
   * changed by editing it). In "create" mode this is normally left unset — the user picks the
   * project from the Project field inside the wizard itself instead, so the dropdown only ever
   * lists projects that already exist. */
  projectIdOverride?: string;
  /** Called after a successful submit in mode="edit", instead of the default create-mode
   * navigation to /projects. */
  onSubmitSuccess?: () => void;
  /** Called when the wizard is cancelled in mode="edit", instead of the default create-mode
   * navigation to /create. */
  onCancelEdit?: () => void;
}

export default function CreateProductPage({
  mode = "create",
  productTypeOverride,
  initialValues,
  projectIdOverride,
  onSubmitSuccess,
  onCancelEdit,
}: CreateProductPageProps = {}) {
  const { productType: productTypeParam } = useParams<{ productType: string }>();
  const productType = productTypeOverride ?? productTypeParam;
  const navigate = useNavigate();
  const schema = getProductSchema(productType || "");
  const isEdit = mode === "edit";
  // In edit mode EditProductPage owns the title ("Edit <stack>"); its parent-level effect runs
  // after this child effect, so pass undefined here to avoid clobbering it.
  useDocumentTitle(isEdit ? undefined : `Create ${schema?.title ?? productType ?? ""}`);

  const [form, setForm] = useState<FormValues>(() => (isEdit && initialValues ? initialValues : defaultsFor(schema)));
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [allProjects, setAllProjects] = useState<{ documentId: string; name: string }[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(projectIdOverride);
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [projectLoading, setProjectLoading] = useState(!!projectIdOverride);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customResultAlert, setCustomResultAlert] = useState<{ synced: number; unsupported: number } | null>(null);

  useEffect(() => {
    setForm(isEdit && initialValues ? initialValues : defaultsFor(schema));
    setActiveStep(0);
    setErrors({});
    setSubmitError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, initialValues]);

  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
    })();
  }, []);

  // In create mode, the Project field lists every existing project to choose from (never
  // freeform), so a product can only ever be attached to a project that already exists.
  useEffect(() => {
    if (isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`/projects?pagination[pageSize]=200&fields[0]=name`);
        const { data } = await res.json();
        setAllProjects((data ?? []).map((p: any) => ({ documentId: p.documentId, name: p.name })));
      } catch {
        // Non-fatal: the Project select just renders empty; the user can retry by reopening the page.
      }
    })();
  }, [isEdit]);

  useEffect(() => {
    if (!selectedProjectId) { setProject(null); setProjectLoading(false); return; }
    setProjectLoading(true);
    setProjectError(null);
    (async () => {
      try {
        const res = await apiFetch(
          `/projects/${selectedProjectId}?fields[0]=name&populate[environments][fields][0]=name&populate[environments][fields][1]=isProduction`
        );
        if (!res.ok) throw new Error(`Failed to load project: ${res.status}`);
        const json = await res.json();
        const data = json.data;
        setProject({ documentId: data.documentId, name: data.name, environments: data.environments ?? [] });
        // In create mode, switching the selected project invalidates any previously chosen
        // environment (it belonged to a different project's environment list), so clear it. In
        // edit mode the environment was already resolved from the stack's own deployment history
        // (see EditProductPage's initialValues) and must be preserved, not reset to empty here.
        setForm((prev) => (isEdit ? { ...prev, projectName: data.name } : { ...prev, projectName: data.name, taggingEnv: "" }));
      } catch (e: any) {
        setProjectError(e.message);
      } finally {
        setProjectLoading(false);
      }
    })();
  }, [selectedProjectId, isEdit]);

  const environmentOptions = (project?.environments ?? []).map((e) => ({ label: e.name, value: e.name }));

  const accountOptions = accounts.map((a) => ({
    label: `${a.Alias} (${a.accountid})`,
    value: a.documentId,
  }));

  const set = (key: string, value: FieldValue) =>
    setForm((prev) => {
      const next: FormValues = { ...prev, [key]: value };
      // If this change hides a toggle that injects env vars (e.g. turning off VPC-Attached
      // hides Use Proxy), strip its injected vars and reset the toggle so re-showing it later
      // starts clean and the payload doesn't retain stale entries.
      if (schema) {
        const allFields = schema.steps.flatMap((s) => s.fields);
        withInjectedEnvVarsCleanup(allFields, next as GroupItem);
      }
      return next;
    });

  const visibleSteps = schema ? schema.steps.filter((step) => isStepVisible(step, form)) : [];

  // A step can disappear out from under the user (e.g. switching Server Type away from ECS while
  // sitting on the "ECS & Scaling Settings" step). Clamp back to the last visible step so the
  // wizard never renders an out-of-range index.
  useEffect(() => {
    if (activeStep > visibleSteps.length) setActiveStep(visibleSteps.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSteps.length]);

  if (!schema) {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[{ text: "Create Product", href: "/create" }, { text: "Not found", href: "#" }]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Alert type="error">Unknown product type &quot;{productType}&quot;.</Alert>
      </SpaceBetween>
    );
  }

  // A "coming soon" product (no configuration wizard built yet, e.g. FM Bundle in step 1 of the
  // rework) has no steps. Rendering the Wizard would break (its onNavigate dereferences
  // visibleSteps[activeStep]) and there'd be nothing to submit, so show a placeholder instead.
  if (schema.steps.length === 0) {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[{ text: "Create Product", href: "/create" }, { text: schema.title, href: "#" }]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Alert type="info" header={`${schema.title} — coming soon`}>
          {schema.description} This product's configuration form isn't available yet.
        </Alert>
      </SpaceBetween>
    );
  }

  // In edit mode the project is always known ahead of time (via projectIdOverride) and its load
  // failing is a hard error — there's no field the user can fix it from. In create mode the
  // project is picked from a field inside the wizard itself, so a load failure there is instead
  // surfaced inline next to that field (see the Project field rendering below).
  if (isEdit && projectLoading) return <Spinner size="large" />;
  if (isEdit && projectError) {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[{ text: "Create Product", href: "/create" }, { text: schema.title, href: "#" }]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Alert type="error">{projectError}</Alert>
      </SpaceBetween>
    );
  }

  const validateStep = (stepIndex: number) => {
    const step = visibleSteps[stepIndex];
    const e: Record<string, string> = {};

    // The Basic Info step (every schema's first step) always has a "projectName" field driven by
    // the Project selector rather than free text — validate it against actual project state
    // rather than just the string, so picking a project with zero environments is also caught
    // here even though the environment field itself isn't required to have a value yet.
    const hasProjectField = step.fields.some((f) => f.key === "projectName");
    if (hasProjectField) {
      if (!project) {
        e.projectName = "Select a project";
      } else if (project.environments.length === 0) {
        e.projectName = `Project "${project.name}" has no environments yet. Create one from the project page first.`;
      }
    }

    for (const field of step.fields) {
      if (!isFieldVisible(field, form)) continue;
      if (field.constant || field.disabled) continue;
      if (field.type === "keyvalue") continue;
      if (field.type === "group-list") {
        const items = (form[field.key] as GroupItem[] | undefined) ?? [];
        const itemFields = field.itemFields ?? [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          for (const f of itemFields) {
            if (!isFieldVisible(f, item) || f.type === "keyvalue" || f.constant || f.disabled) continue;
            const isReq = f.required || (f.requiredWhenAnyTrue?.some((key) => item[key] === true) ?? false);
            if (isReq) {
              const v = item[f.key];
              if (v === undefined || v === "" || v === null) {
                e[field.key] = `Item ${i + 1}: "${f.label}" is required.`;
              }
            }
          }
        }
        continue;
      }
      const isRequired = field.required || (field.requiredWhenAnyTrue?.some((key) => form[key] === true) ?? false);
      if (isRequired) {
        const value = form[field.key];
        if (value === undefined || value === "" || value === null) e[field.key] = "Required";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const payload: Record<string, any> = {};
    for (const step of visibleSteps) {
      for (const field of step.fields) {
        if (!isFieldVisible(field, form)) continue;
        if (field.injectsEnvVars) continue; // control-only field; its effect is already merged into the target keyvalue field
        if (field.disabled) continue; // placeholder field with no real value to send yet
        buildFieldPayload(field, form[field.key], payload);
      }
    }
    // ICLProject and ICLEnv are always derived from projectName/taggingEnv rather than
    // collected as separate inputs, so every product gets these tags automatically. The
    // server (see services/stack-naming.ts) re-derives and enforces the same tags/stack name
    // from projectName + envName, so this is purely for immediate UI feedback (Review step).
    if (form.projectName) setAtPath(payload, "tags.ICLProject", form.projectName);
    if (form.taggingEnv) setAtPath(payload, "tags.ICLEnv", form.taggingEnv);
    return payload;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    setCustomResultAlert(null);
    try {
      if (schema.productType === "custom") {
        // Custom product: call the create-with-discovery endpoint directly
        const selectedAccountObj = accounts.find((a) => a.documentId === form.accountId);
        if (!selectedAccountObj) throw new Error("Please select an account.");
        const creds = getAwsCreds(selectedAccountObj.Alias);
        if (!creds) throw new Error(`AWS credentials not configured for '${selectedAccountObj.Alias}' account. Go to Settings to set them up.`);

        const res = await apiFetch(`/custom-products/create-with-discovery`, {
          method: "POST",
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
          body: JSON.stringify({
            name: form.name,
            description: form.description || undefined,
            tagKey: form.tagKey,
            tagValue: form.tagValue,
            accountId: form.accountId,
            projectId: selectedProjectId || project?.documentId,
            region: getDefaultRegion(),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error?.message || `Request failed: ${res.status}`);
        }
        const data = await res.json();
        const { product, results } = data;
        setCustomResultAlert({ synced: results?.synced ?? 0, unsupported: results?.unsupported ?? 0 });
        // Navigate after a brief delay so the user can see the result alert
        setTimeout(() => navigate(`/products/custom/${product.documentId}`), 1500);
      } else {
        const creds = getAwsCreds("appsb");
        if (!creds) throw new Error("AWS credentials not configured for 'appsb' account. Go to Settings to set them up.");

        const fullPayload = buildPayload();
        const { projectName, accountId, ...payload } = fullPayload;

        const res = await apiFetch(`/deployment-requests/submit`, {
          method: "POST",
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
          body: JSON.stringify({
            projectName,
            productType: schema.productType,
            accountId,
            envName: form.taggingEnv,
            payload,
          }),
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        if (isEdit && onSubmitSuccess) onSubmitSuccess();
        else navigate(project ? `/projects/${project.documentId}` : "/projects");
      }
    } catch (e: any) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Renders a single field's control against an arbitrary values/updater pair, so the exact same
   * logic works for the top-level form and for each item inside a "group-list" field.
   * `siblingFields` is the field set the value lives among (top-level schema fields, or a group-list's itemFields),
   * used to resolve optionsByField/injectsEnvVars/protected-keyvalue-keys relative to the right scope.
   */
  const renderFieldWith = (
    field: FieldSchema,
    values: GroupItem,
    updateValue: (key: string, value: FieldValue) => void,
    siblingFields: FieldSchema[],
    fieldErrors: Record<string, string>,
    isTopLevel: boolean
  ): ReactElement | null => {
    if (!isFieldVisible(field, values)) return null;
    if (field.constant) return null;

    // Project Name is never typed freeform. In edit mode it's fixed to the stack's existing
    // project (locked, read-only) since changing it would target a different/nonexistent stack.
    // In create mode it's chosen from every existing project via a Select — see the dedicated
    // rendering below — so a product can only ever be attached to a project that already exists.
    const isLocked =
      (isEdit && field.key === "projectName" && !!project) ||
      (isEdit && isTopLevel && field.key === "accountId" && !!values[field.key]);

    if (isTopLevel && field.key === "accountId") {
      return (
        <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
          <Select
            selectedOption={accountOptions.find((o) => o.value === values.accountId) || null}
            onChange={({ detail }) => updateValue("accountId", detail.selectedOption.value!)}
            options={accountOptions}
            placeholder="Select account"
            filteringType="auto"
            disabled={isLocked}
          />
        </FormField>
      );
    }

    // In edit mode, Project Name is fixed to the stack's existing project — shown read-only.
    if (isTopLevel && field.key === "projectName" && isEdit && project) {
      return (
        <FormField key={field.key} label={field.label} description={field.description}>
          <Input value={project.name} disabled />
        </FormField>
      );
    }

    // In create mode, Project is picked from every existing project (never typed freeform), so
    // a product can only be created for a project that already exists. Selecting a project loads
    // its Environments for the Environment field below, and clears any previously selected
    // environment (it belonged to whichever project was selected before, if any).
    if (isTopLevel && field.key === "projectName" && !isEdit) {
      const projectSelectOptions = allProjects.map((p) => ({ value: p.documentId, label: p.name }));
      return (
        <FormField
          key={field.key}
          label={field.label}
          description="The project this product belongs to. Only existing projects can be selected — create one from the Projects page first if it's missing."
          errorText={fieldErrors.projectName ?? (projectError || undefined)}
        >
          <Select
            selectedOption={project ? { value: project.documentId, label: project.name } : null}
            onChange={({ detail }) => setSelectedProjectId(detail.selectedOption.value!)}
            options={projectSelectOptions}
            placeholder="Select project"
            filteringType="auto"
            loadingText="Loading projects..."
            empty="No projects found. Create one from the Projects page first."
          />
        </FormField>
      );
    }

    // Environment is selected from the project's existing Environments (created on the project
    // page) rather than a static Prod/Dev/Qa/Staging list, so the same product can be deployed
    // under multiple environments without the stack name/tags colliding (see stack-naming.ts).
    if (isTopLevel && field.key === "taggingEnv") {
      return (
        <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
          <Select
            selectedOption={environmentOptions.find((o) => o.value === values.taggingEnv) || null}
            onChange={({ detail }) => updateValue("taggingEnv", detail.selectedOption.value!)}
            options={environmentOptions}
            placeholder="Select environment"
            disabled={isLocked}
          />
        </FormField>
      );
    }

    switch (field.type) {
      case "select": {
        const resolvedOptions = field.optionsByField
          ? field.optionsByField.options[String(values[field.optionsByField.key] ?? "")] ?? []
          : field.options || [];
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <Select
              selectedOption={resolvedOptions.find((o) => o.value === values[field.key]) || null}
              onChange={({ detail }) => updateValue(field.key, detail.selectedOption.value!)}
              options={resolvedOptions}
              placeholder={`Select ${field.label.toLowerCase()}`}
              disabled={field.disabled}
            />
          </FormField>
        );
      }
      case "toggle":
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <Toggle
              checked={!!values[field.key]}
              disabled={field.disabled}
              onChange={({ detail }) => {
                updateValue(field.key, detail.checked);
                if (field.injectsEnvVars) {
                  const { targetKey, vars } = field.injectsEnvVars;
                  const currentPairs = (values[targetKey] as KeyValuePair[] | undefined) ?? [];
                  const varKeys = Object.keys(vars);
                  if (detail.checked) {
                    const withoutInjected = currentPairs.filter((p) => !varKeys.includes(p.key));
                    const injectedPairs = varKeys.map((k) => ({ key: k, value: vars[k] }));
                    updateValue(targetKey, [...withoutInjected, ...injectedPairs]);
                  } else {
                    updateValue(targetKey, currentPairs.filter((p) => !varKeys.includes(p.key)));
                  }
                }
              }}
            >
              {values[field.key] ? "Yes" : "No"}
            </Toggle>
          </FormField>
        );
      case "number":
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <Input type="number" value={String(values[field.key] ?? "")} onChange={({ detail }) => updateValue(field.key, detail.value)} disabled={field.disabled} />
          </FormField>
        );
      case "keyvalue": {
        const allPairs = (values[field.key] as KeyValuePair[] | undefined) ?? [];
        // Keys injected by an active toggle (e.g. Use Proxy) are managed exclusively by that
        // toggle: they're shown as fixed, non-interactive rows and excluded from the editable
        // list entirely, so there's no remove button or edit control for them at all — only the
        // toggle can add or remove them.
        const protectedKeys = new Set<string>();
        for (const f of siblingFields) {
          if (f.injectsEnvVars?.targetKey === field.key && values[f.key]) {
            for (const k of Object.keys(f.injectsEnvVars.vars)) protectedKeys.add(k);
          }
        }
        const fixedPairs = allPairs.filter((p) => protectedKeys.has(p.key));
        const pairs = allPairs.filter((p) => !protectedKeys.has(p.key));
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <SpaceBetween size="s">
              {fixedPairs.length > 0 && (
                <ColumnLayout columns={2} variant="text-grid">
                  {fixedPairs.map((p) => (
                    <div key={p.key}>
                      <Box variant="awsui-key-label">{p.key}</Box>
                      <Box>{p.value}</Box>
                    </div>
                  ))}
                </ColumnLayout>
              )}
              <AttributeEditor<KeyValuePair>
                items={pairs}
                addButtonText="Add environment variable"
                removeButtonText="Remove"
                onAddButtonClick={() => updateValue(field.key, [...allPairs, { key: "", value: "" }])}
                onRemoveButtonClick={({ detail }) => {
                  const next = pairs.filter((_, i) => i !== detail.itemIndex);
                  updateValue(field.key, [...fixedPairs, ...next]);
                }}
                definition={[
                  {
                    label: "Name",
                    control: (item, itemIndex) => (
                      <Input
                        value={item.key}
                        placeholder="e.g. LOG_LEVEL"
                        onChange={({ detail }) => {
                          const next = pairs.map((p, i) => (i === itemIndex ? { ...p, key: detail.value } : p));
                          updateValue(field.key, [...fixedPairs, ...next]);
                        }}
                      />
                    ),
                  },
                  {
                    label: "Value",
                    control: (item, itemIndex) => (
                      <Input
                        value={item.value}
                        placeholder="e.g. debug"
                        onChange={({ detail }) => {
                          const next = pairs.map((p, i) => (i === itemIndex ? { ...p, value: detail.value } : p));
                          updateValue(field.key, [...fixedPairs, ...next]);
                        }}
                      />
                    ),
                  },
                ]}
                empty="No environment variables added."
              />
            </SpaceBetween>
          </FormField>
        );
      }
      case "group-list": {
        const items = (values[field.key] as GroupItem[] | undefined) ?? [];
        const itemFields = field.itemFields ?? [];
        const setItems = (next: GroupItem[]) => updateValue(field.key, next);
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <SpaceBetween size="m">
              {items.map((item, index) => {
                const title = (field.itemTitleField && (item[field.itemTitleField] as string)) || field.newItemLabel || "New item";
                // `pendingItem` accumulates changes made by multiple updateItemValue calls fired
                // synchronously within the same event handler (e.g. the Use Proxy toggle updating
                // both its own value and the injected environmentVariables in one onChange). Without
                // this, each call would compute its `nextItem` from the same stale `item` snapshot
                // and the second call's setItems would overwrite the first call's change.
                let pendingItem = item;
                const updateItemValue = (key: string, value: FieldValue) => {
                  const nextItem: GroupItem = { ...pendingItem, [key]: value };
                  withInjectedEnvVarsCleanup(itemFields, nextItem);
                  pendingItem = nextItem;
                  const nextItems = items.map((it, i) => (i === index ? nextItem : it));
                  setItems(nextItems);
                };
                return (
                  <ExpandableSection key={index} headerText={title} defaultExpanded variant="container">
                    <SpaceBetween size="m">
                      {itemFields.map((f) => renderFieldWith(f, item, updateItemValue, itemFields, {}, false))}
                      <Button
                        iconName="remove"
                        onClick={() => setItems(items.filter((_, i) => i !== index))}
                      >
                        Remove environment
                      </Button>
                    </SpaceBetween>
                  </ExpandableSection>
                );
              })}
              <Button iconName="add-plus" onClick={() => setItems([...items, defaultsForFields(itemFields)])}>
                {field.addItemLabel || "Add item"}
              </Button>
            </SpaceBetween>
          </FormField>
        );
      }
      default:
        return (
          <FormField key={field.key} label={field.label} description={field.description} errorText={fieldErrors[field.key]}>
            <Input value={String(values[field.key] ?? "")} onChange={({ detail }) => updateValue(field.key, detail.value)} disabled={isLocked || field.disabled} />
          </FormField>
        );
    }
  };

  const renderField = (field: FieldSchema) => {
    const topLevelFields = schema.steps.flatMap((s) => s.fields);
    return renderFieldWith(field, form as GroupItem, set, topLevelFields, errors, true);
  };

  const selectedAccount = accounts.find((a) => a.documentId === form.accountId);

  const reviewSteps = visibleSteps.map((step) => ({
    title: step.title,
    fields: step.fields.filter((f) => isFieldVisible(f, form) && !f.constant && !f.disabled),
  }));

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={
          isEdit
            ? [
                { text: "Projects", href: "/projects" },
                ...(project ? [{ text: project.name, href: `/projects/${project.documentId}` }] : []),
                { text: `Edit / Redeploy — ${schema.title}`, href: "#" },
              ]
            : [
                { text: "Create Product", href: "/create" },
                { text: schema.title, href: "#" },
              ]
        }
        onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
      />
      {!schema.available && (
        <Alert type="info">
          This product's full configuration form isn't built yet. You can still submit a request with basic info below.
        </Alert>
      )}
      <Wizard
        i18nStrings={{
          stepNumberLabel: (n) => `Step ${n}`,
          collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
          submitButton: isEdit ? "Redeploy" : "Submit",
          previousButton: "Previous",
          nextButton: "Next",
          cancelButton: "Cancel",
          optional: "optional",
        }}
        activeStepIndex={activeStep}
        onNavigate={({ detail }) => {
          if (detail.requestedStepIndex > activeStep && !visibleSteps[activeStep].isOptional && !validateStep(activeStep)) return;
          setErrors({});
          setActiveStep(Math.min(detail.requestedStepIndex, visibleSteps.length));
        }}
        onCancel={() => (isEdit ? onCancelEdit?.() : navigate("/create"))}
        onSubmit={handleSubmit}
        isLoadingNextStep={submitting}
        steps={[
          ...visibleSteps.map((step) => ({
            title: step.title,
            isOptional: step.isOptional,
            content: (
              <Container header={<Header variant="h2">{step.title}</Header>}>
                <SpaceBetween size="m">
                  {step.fields.map((field) => renderField(field))}
                </SpaceBetween>
              </Container>
            ),
          })),
          {
            title: "Review & Submit",
            content: (
              <SpaceBetween size="l">
                {submitError && <Alert type="error">{submitError}</Alert>}
                {customResultAlert && (
                  <Alert type="success">
                    Discovery complete: {customResultAlert.synced} resource(s) synced, {customResultAlert.unsupported} unsupported.
                  </Alert>
                )}
                <Container header={<Header variant="h2">Review</Header>}>
                  <SpaceBetween size="l">
                    {reviewSteps.map((step) => (
                      <div key={step.title}>
                        <Header variant="h3">{step.title}</Header>
                        <ColumnLayout columns={2} variant="text-grid">
                          {step.fields.map((field) => (
                            <div key={field.key}>
                              <Box variant="awsui-key-label">{field.label}</Box>
                              <Box>
                                {field.key === "accountId"
                                  ? selectedAccount ? `${selectedAccount.Alias} (${selectedAccount.accountid})` : "-"
                                  : field.type === "toggle"
                                  ? (form[field.key] ? "Yes" : "No")
                                  : field.type === "keyvalue"
                                  ? (() => {
                                      const pairs = ((form[field.key] as KeyValuePair[] | undefined) ?? []).filter((p) => p.key !== "");
                                      return pairs.length === 0 ? "-" : pairs.map((p) => `${p.key}=${p.value}`).join(", ");
                                    })()
                                  : field.type === "group-list"
                                  ? (() => {
                                      const items = (form[field.key] as GroupItem[] | undefined) ?? [];
                                      if (items.length === 0) return "-";
                                      return (
                                        <SpaceBetween size="xs">
                                          {items.map((item, i) => {
                                            const title = (field.itemTitleField && (item[field.itemTitleField] as string)) || `Item ${i + 1}`;
                                            const summary = (field.itemFields ?? [])
                                              .filter((f) => isFieldVisible(f, item) && f.type !== "keyvalue" && item[f.key] !== "" && item[f.key] !== undefined)
                                              .map((f) => `${f.label}: ${f.type === "toggle" ? (item[f.key] ? "Yes" : "No") : String(item[f.key])}`)
                                              .join(", ");
                                            return (
                                              <Box key={i}>
                                                <strong>{title}</strong>
                                                {summary ? ` — ${summary}` : ""}
                                              </Box>
                                            );
                                          })}
                                        </SpaceBetween>
                                      );
                                    })()
                                  : String(form[field.key] ?? "-")}
                              </Box>
                            </div>
                          ))}
                        </ColumnLayout>
                      </div>
                    ))}
                  </SpaceBetween>
                </Container>
              </SpaceBetween>
            ),
          },
        ]}
      />
    </SpaceBetween>
  );
}
